import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Footnote,
    styles as typographyStyles,
} from "@khanacademy/wonder-blocks-typography";
import {useGuideState} from "@repo/guide-state";
import type {Message} from "@repo/guide-types";

import {MessageIcon} from "../icons/message-icon.tsx";

import {sharedStyles} from "./message-and-context-bubble-shared-styles.ts";
import {MessageContent} from "./message-content.tsx";

export interface Props {
    message: Readonly<Message>;
    styleAsStudentMode: boolean;
}

const ContextBubble = (props: Props): React.ReactElement => {
    const {message, styleAsStudentMode} = props;
    const {selectedContext, contentFlagged, interactionId, links} = message;
    const swagId = useGuideState("swagId");
    const isOffline = useGuideState("isOffline");

    return (
        <li
            data-testid="context-bubble"
            className={css(
                sharedStyles.outerWrapper,
                selectedContext && styles.selectedContextWrapper,
            )}
        >
            <View style={[styles.selectedContextFootnote]}>
                <MessageIcon
                    id={swagId}
                    isOffline={isOffline}
                    reaction={null}
                    style={sharedStyles.shrunkenIcon}
                />
                <Footnote style={[styles.footnote]}>
                    {t`Text from your selection`}
                </Footnote>
            </View>
            <View
                style={[
                    sharedStyles.messageBubble,
                    typographyStyles.Body,
                    styleAsStudentMode &&
                        sharedStyles.botMessageTeacherAsStudentMode,
                    styles.selectedContextBubble,
                ]}
                testId="ai-message"
            >
                <MessageContent
                    text={selectedContext || ""}
                    links={links}
                    interactionId={interactionId}
                    shouldShowCursor={false}
                    isIncoming={true}
                    contentFlagged={contentFlagged}
                    hasSelectedContext={true}
                />
            </View>
        </li>
    );
};

export default ContextBubble;

const styles = StyleSheet.create({
    selectedContextBubble: {
        backgroundColor: color.offBlack8,
        borderWidth: "1px 1px 1px 6px",
        borderColor: color.offBlack32,
    },
    /** negative margin needed to keep text aligned with icon styled with
    same marginLeft !important */
    selectedContextFootnote: {
        flexDirection: "row",
        gap: 0,
        marginBlockEnd: -spacing.small_12,
    },
    footnote: {
        marginInlineStart: -spacing.xxSmall_6,
    },
    selectedContextWrapper: {
        flexDirection: "column",
        gap: spacing.medium_16,
    },
});
